<template>
<div class="spg">
    <!-- List -->
    <div v-if="!editing">
        <div class="spg-top">
            <div>
                <h3 class="mb-1 font-weight-bold text-body">Pages</h3>
                <p class="text-muted mb-0">Every information page of the site. Click a page to change its text. Links to pages are set in Header &amp; Footer.</p>
            </div>
            <button type="button" class="btn btn-primary" @click="startNew">+ New page</button>
        </div>

        <div class="spg-list">
            <div v-for="page in pages" :key="page.id" class="spg-row" @click="open(page)">
                <div class="spg-row__main">
                    <strong>{{ page.title }}</strong>
                    <span class="text-muted small">{{ decode(page.url) }}</span>
                </div>
                <span class="spg-tag" :class="page.kind === 'designed' ? 'is-designed' : 'is-text'">{{ { designed: 'Form page (header only)', blocks: 'Sections page', text: 'Text page' }[page.kind] }}</span>
                <span class="spg-tag" :class="page.in_footer ? 'is-footer' : 'is-nofooter'">{{ page.in_footer ? 'In the footer' : 'Not linked in the footer' }}</span>
                <div class="spg-row__actions" @click.stop>
                    <a :href="page.full_url" target="_blank" rel="noopener" class="btn btn-sm btn-light">View</a>
                    <button type="button" class="btn btn-sm btn-outline-primary" @click="open(page)">Edit</button>
                    <button v-if="!page.system" type="button" class="btn btn-sm btn-light text-danger" @click="askDelete(page)">Delete</button>
                </div>
            </div>
            <p v-if="!pages.length" class="text-muted p-3 mb-0">Loading…</p>
        </div>

        <div v-if="deleting" class="spg-confirm">
            <span>Delete <b>{{ deleting.title }}</b>? Its address will stop working and its footer link is removed.</span>
            <button type="button" class="btn btn-sm btn-danger" @click="doDelete">Delete</button>
            <button type="button" class="btn btn-sm btn-light" @click="deleting = null">Cancel</button>
        </div>
    </div>

    <!-- Editor -->
    <div v-else>
        <div class="spg-top">
            <div>
                <a href="javascript:void(0)" @click="close">← All pages</a>
                <h3 class="mb-0 mt-1 font-weight-bold text-body">{{ form.id ? form.title || 'Page' : 'New page' }}</h3>
            </div>
            <div class="d-flex align-items-center" style="gap: 10px;">
                <a v-if="form.full_url" :href="form.full_url" target="_blank" rel="noopener" class="btn btn-light">View on the site</a>
                <button type="button" class="btn btn-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
            </div>
        </div>

        <div class="row">
            <div class="col-xl-8">
                <div class="spg-card">
                    <h5>Page header</h5>
                    <p class="text-muted small">The top part of the page: a small line, the big title and a short introduction.</p>
                    <div class="form-group">
                        <label>Page name <span class="text-danger">*</span></label>
                        <input v-model="form.title" type="text" class="form-control" maxlength="190">
                        <small class="form-text text-muted">Used in the browser tab and the "Home / …" path above the title.</small>
                    </div>
                    <div class="form-group">
                        <label>Small line above the title</label>
                        <input v-model="form.eyebrow" type="text" class="form-control" maxlength="190" placeholder="e.g. Customer care">
                    </div>
                    <div class="form-group">
                        <label>Big title</label>
                        <input v-model="form.headline" type="text" class="form-control" maxlength="250" :placeholder="'Empty = the page name (' + (form.title || '…') + ')'">
                    </div>
                    <div class="form-group mb-0">
                        <label>Introduction</label>
                        <textarea v-model="form.lead" class="form-control" rows="2" maxlength="1000"></textarea>
                    </div>
                </div>

                <div v-if="form.kind === 'designed'" class="spg-card spg-note">
                    <h5>Rest of the page</h5>
                    <p class="mb-0">This page has a special layout ({{ designedWhat }}), so only its header is edited here. Everything else on it stays as designed.</p>
                </div>

                <template v-else-if="form.kind === 'blocks'">
                    <div class="spg-card">
                        <h5>Page sections</h5>
                        <p class="text-muted small mb-3">
                            Everything under the page header, top to bottom. Click a section to edit it; use the arrows to move it.
                            In longer texts an empty line starts a new paragraph, <code>[text](/address)</code> makes a link and
                            <code>{phone}</code> / <code>{email}</code> show your phone and email as links.
                        </p>

                        <div v-for="(block, bi) in form.blocks" :key="bi" class="spg-block" :class="{ 'is-open': openBlock === bi }">
                            <div class="spg-block__head" @click="openBlock = openBlock === bi ? null : bi">
                                <span class="spg-block__num">{{ bi + 1 }}</span>
                                <div class="spg-block__title">
                                    <small>{{ blockTypes[block.type] ? blockTypes[block.type].label : block.type }}</small>
                                    <strong>{{ block.title || block.eyebrow || (blockTypes[block.type] && blockTypes[block.type].hint) || '—' }}</strong>
                                </div>
                                <div class="spg-block__tools" @click.stop>
                                    <button type="button" class="btn btn-sm btn-light" title="Move up" :disabled="bi === 0" @click="moveBlock(bi, -1)">↑</button>
                                    <button type="button" class="btn btn-sm btn-light" title="Move down" :disabled="bi === form.blocks.length - 1" @click="moveBlock(bi, 1)">↓</button>
                                    <button type="button" class="btn btn-sm btn-light text-danger" title="Remove this section" @click="removeBlock(bi)">✕</button>
                                </div>
                            </div>

                            <div v-if="openBlock === bi && blockTypes[block.type]" class="spg-block__body">
                                <p v-if="blockTypes[block.type].help" class="text-muted small">{{ blockTypes[block.type].help }}</p>
                                <div v-for="f in blockTypes[block.type].fields" :key="f.key" class="form-group">
                                    <block-field :field="f" :target="block" @pick="pickImage(block, f.key)" :image-url="imageUrl"></block-field>
                                </div>

                                <template v-if="blockTypes[block.type].items">
                                    <label class="font-weight-bold d-block">{{ blockTypes[block.type].itemsLabel }}</label>
                                    <div v-for="(item, ii) in block.items" :key="ii" class="spg-item">
                                        <div class="spg-item__head">
                                            <strong>{{ ii + 1 }}. {{ item.title || 'New item' }}</strong>
                                            <span>
                                                <button type="button" class="btn btn-sm btn-light" :disabled="ii === 0" @click="moveItem(block, ii, -1)">↑</button>
                                                <button type="button" class="btn btn-sm btn-light" :disabled="ii === block.items.length - 1" @click="moveItem(block, ii, 1)">↓</button>
                                                <button type="button" class="btn btn-sm btn-light text-danger" @click="block.items.splice(ii, 1)">✕</button>
                                            </span>
                                        </div>
                                        <div v-for="f in blockTypes[block.type].items" :key="f.key" class="form-group mb-2">
                                            <block-field :field="f" :target="item" :image-url="imageUrl"></block-field>
                                        </div>
                                    </div>
                                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="addItem(block)">+ Add {{ blockTypes[block.type].itemName }}</button>
                                </template>
                            </div>
                        </div>

                        <div class="spg-add">
                            <select v-model="newBlockType" class="form-control">
                                <option value="">Add a section…</option>
                                <option v-for="(t, key) in blockTypes" :key="key" :value="key">{{ t.label }} — {{ t.hint }}</option>
                            </select>
                            <button type="button" class="btn btn-outline-primary" :disabled="!newBlockType" @click="addBlock">Add</button>
                        </div>
                    </div>
                    <attach-image @toggleImageSelect="showPicker = !showPicker" :showModal="showPicker" @setImage="setImage"/>
                </template>

                <template v-else>
                    <div class="spg-card">
                        <h5>Page text</h5>
                        <p class="text-muted small">Use the toolbar for headings, lists, bold text and links. <code>{phone}</code> and <code>{email}</code> show your phone number and email.</p>
                        <vue-editor v-model="form.body" :editor-toolbar="toolbar"></vue-editor>
                    </div>

                    <div class="spg-card">
                        <h5>Highlight boxes <small class="text-muted">(optional)</small></h5>
                        <p class="text-muted small">Big boxes above the text. One per line: <code>big text | small text</code>, e.g. <code>1 year | Price protection after your purchase</code>.</p>
                        <textarea v-model="form.highlights" class="form-control" rows="3" maxlength="2000"></textarea>
                    </div>

                    <div class="spg-card">
                        <h5>Buttons under the text <small class="text-muted">(optional)</small></h5>
                        <div v-for="(b, i) in form.buttons" :key="i" class="spg-button">
                            <input v-model="b.label" type="text" class="form-control" placeholder="Button text" maxlength="60">
                            <input v-model="b.url" type="text" class="form-control" placeholder="/shop or https://…" maxlength="500">
                            <button type="button" class="btn btn-sm btn-light text-danger" @click="form.buttons.splice(i, 1)">✕</button>
                        </div>
                        <button v-if="form.buttons.length < 4" type="button" class="btn btn-sm btn-outline-secondary" @click="form.buttons.push({ label: '', url: '' })">+ Add a button</button>
                    </div>
                </template>
            </div>

            <div class="col-xl-4">
                <div class="spg-card">
                    <h5>Address</h5>
                    <template v-if="form.system_key">
                        <p class="mb-1"><code>{{ decode(form.url) }}</code></p>
                        <small class="text-muted">Built-in page: its address can't change.</small>
                    </template>
                    <template v-else>
                        <div class="input-group">
                            <div class="input-group-prepend"><span class="input-group-text">/page/</span></div>
                            <input v-model="form.slug" type="text" class="form-control" maxlength="120" placeholder="made from the name">
                        </div>
                        <small class="form-text text-muted">Letters, numbers and dashes. Empty = made from the page name.</small>
                    </template>
                </div>

                <div class="spg-card">
                    <h5>Footer link</h5>
                    <p v-if="form.in_footer" class="mb-1 text-success">This page is linked in the footer.</p>
                    <template v-else-if="!form.id">
                        <label class="d-block small text-muted">Add a link to this page in the footer:</label>
                        <select v-model="form.footer_column" class="form-control">
                            <option value="">No footer link</option>
                            <option value="help">Second column (Help)</option>
                            <option value="company">Third column (Company)</option>
                            <option value="legal">Small links at the bottom</option>
                        </select>
                    </template>
                    <p v-else class="mb-1 text-muted">Not linked in the footer.</p>
                    <router-link to="/admin/content/header-footer" class="small">Edit the footer links →</router-link>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import { VueEditor } from 'vue2-editor';
import BlockField from './BlockField.vue';

const EMPTY = { id: null, system_key: null, kind: 'text', title: '', eyebrow: '', headline: '', lead: '', body: '', highlights: '', buttons: [], blocks: [], slug: '', url: '', full_url: '', in_footer: false, footer_column: '' };
const DESIGNED = { careers: 'job application form', contact: 'contact form' };

// Section types of the About / Delivery pages (drawn by resources/views/includes/coastal-pages/blocks.blade.php)
const T = (key, label, extra) => Object.assign({ key, label, type: 'text' }, extra || {});
const TEXT_HELP = 'Empty line = new paragraph. [text](/address) = link.';
const BLOCK_TYPES = {
    story: {
        label: 'Story with picture', hint: 'picture beside a title, text and buttons',
        fields: [T('image', 'Picture', { type: 'image' }), T('eyebrow', 'Small line above the title'), T('title', 'Title'),
            T('text', 'Text', { type: 'textarea', rows: 6, help: TEXT_HELP }), T('buttons', 'Buttons', { type: 'buttons' })],
    },
    feature: {
        label: 'Picture + text + list', hint: 'a delivery option with a picture and a list',
        fields: [T('image', 'Picture', { type: 'image', help: 'No picture = a box drawing with the caption below.' }),
            T('art_caption', 'Caption under the box drawing (when there is no picture)'), T('eyebrow', 'Small line above the title'), T('title', 'Title'),
            T('nav_label', 'Short name in the "On this page" links', { help: 'Empty = the title.' }),
            T('text', 'Text', { type: 'textarea', help: TEXT_HELP }), T('list_title', 'List title (e.g. "What you\u2019ll get")'),
            T('list', 'List (one item per line)', { type: 'lines', rows: 6 }), T('numbered', 'Numbered steps (1, 2, 3) instead of bullets', { type: 'check' }),
            T('flip', 'Picture on the right', { type: 'check' })],
    },
    values: {
        label: 'Boxes with icons', hint: 'a row of short reasons with icons and links',
        fields: [T('eyebrow', 'Small line above the title'), T('title', 'Title')],
        itemsLabel: 'Boxes', itemName: 'a box',
        items: [T('icon', 'Icon', { type: 'select', options: { shield: 'Shield (guarantee)', truck: 'Truck (delivery)', card: 'Card (financing)', headset: 'Headset (support)', bed: 'Bed', star: 'Star', tag: 'Price tag', home: 'House' } }),
            T('title', 'Title'), T('text', 'Text', { type: 'textarea', rows: 2 }), T('link_label', 'Link text'), T('link_url', 'Link', { type: 'url' })],
    },
    cards: {
        label: 'Information cards', hint: 'white cards with a title, text and a list',
        fields: [T('eyebrow', 'Small line above the title'), T('title', 'Title')],
        itemsLabel: 'Cards', itemName: 'a card',
        items: [T('title', 'Title'), T('nav_label', 'Short name in the "On this page" links'), T('text', 'Text', { type: 'textarea', rows: 3, help: TEXT_HELP }),
            T('list_title', 'List title'), T('list', 'List (one item per line)', { type: 'lines', rows: 5 })],
    },
    band: {
        label: 'Dark banner', hint: 'black box with a title, text and a button',
        fields: [T('eyebrow', 'Small line above the title'), T('title', 'Title'), T('nav_label', 'Short name in the "On this page" links'),
            T('text', 'Text', { type: 'textarea', rows: 3, help: TEXT_HELP + ' {phone} / {email} = your phone / email.' }),
            T('button_label', 'Button text'), T('button_url', 'Button link', { type: 'url' })],
    },
    notice: {
        label: 'Small notice box', hint: 'one line with a button (e.g. track your order)',
        fields: [T('title', 'Title'), T('text', 'Text'), T('button_label', 'Button text'), T('button_url', 'Button link', { type: 'url' })],
    },
    jump: {
        label: '"On this page" links', hint: 'links to the sections below',
        help: 'Made automatically from the titles of the picture + text sections, dark banners and cards.', fields: [],
    },
};

export default {
    components: { VueEditor, BlockField },
    data() {
        return {
            pages: [], editing: false, saving: false, deleting: null, savedSnapshot: '',
            blockTypes: BLOCK_TYPES, openBlock: null, newBlockType: '', showPicker: false, pickerTarget: null,
            form: JSON.parse(JSON.stringify(EMPTY)),
            toolbar: [[{ header: [2, 3, false] }], ['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']],
        };
    },
    computed: {
        designedWhat() { return DESIGNED[this.form.system_key] || 'custom design'; },
        dirty() { return this.editing && JSON.stringify(this.form) !== this.savedSnapshot; },
    },
    methods: {
        auth() { return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }; },
        decode(url) { try { return decodeURIComponent(url); } catch (e) { return url; } },
        load() {
            axios.get('/api/admin/site-pages', this.auth())
                .then(res => { this.pages = res.data.data; })
                .catch(() => this.$toaster.error('Could not load the pages.'));
        },
        open(page) {
            this.$parent.loading = true;
            axios.get('/api/admin/site-pages/' + page.id, this.auth())
                .then(res => {
                    const p = res.data.data;
                    this.form = Object.assign(JSON.parse(JSON.stringify(EMPTY)), {
                        id: p.id, system_key: p.system_key, kind: p.kind, title: p.title, eyebrow: p.eyebrow, headline: p.headline,
                        lead: p.lead, body: p.body, highlights: p.highlights, buttons: p.buttons || [], slug: p.slug,
                        blocks: JSON.parse(JSON.stringify(p.blocks || [])),
                        url: p.url, full_url: p.full_url, in_footer: p.in_footer,
                    });
                    this.editing = true;
                    // the editor normalizes the HTML once it renders; compare against that
                    this.$nextTick(() => setTimeout(() => { this.savedSnapshot = JSON.stringify(this.form); }, 300));
                })
                .finally(() => (this.$parent.loading = false));
        },
        startNew() {
            this.form = JSON.parse(JSON.stringify(EMPTY));
            this.form.footer_column = 'company';
            this.editing = true;
            this.savedSnapshot = '';
        },
        close() {
            if (this.dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
            this.editing = false;
            this.load();
        },
        save() {
            if (!this.form.title.trim()) { this.$toaster.error('Give the page a name.'); return; }
            const payload = {
                title: this.form.title, eyebrow: this.form.eyebrow, headline: this.form.headline, lead: this.form.lead,
                body: this.form.body, highlights: this.form.highlights, buttons: this.form.buttons.filter(b => b.label.trim()),
                slug: this.form.slug, footer_column: this.form.id ? null : this.form.footer_column || null,
            };
            if (this.form.kind === 'blocks') payload.blocks = this.form.blocks;
            this.saving = true;
            const req = this.form.id
                ? axios.put('/api/admin/site-pages/' + this.form.id, payload, this.auth())
                : axios.post('/api/admin/site-pages', payload, this.auth());
            req.then(res => {
                    this.$toaster.success(res.data.message || 'Saved');
                    const id = this.form.id || (res.data.data && res.data.data.id);
                    this.open({ id });
                })
                .catch(err => {
                    const d = err.response && err.response.data;
                    const first = d && d.errors ? Object.values(d.errors)[0][0] : null;
                    this.$toaster.error(first || (d && d.message) || 'Could not save the page.');
                })
                .finally(() => (this.saving = false));
        },
        askDelete(page) { this.deleting = page; },

        imageUrl(path) {
            if (!path) return '';
            if (/^(https?:)?\/\//i.test(path)) return path;
            if (/^\/?(images|assets)\//.test(path)) return '/' + path.replace(/^\//, '');
            return this.cdnUrl(path);
        },
        pickImage(target, key) { this.pickerTarget = { target, key }; this.showPicker = true; },
        setImage(gallary) {
            if (this.pickerTarget && gallary) this.$set(this.pickerTarget.target, this.pickerTarget.key, gallary.gallary_path);
        },
        moveBlock(i, dir) {
            const b = this.form.blocks.splice(i, 1)[0];
            this.form.blocks.splice(i + dir, 0, b);
            if (this.openBlock === i) this.openBlock = i + dir;
        },
        removeBlock(i) {
            const b = this.form.blocks[i];
            if (!window.confirm('Remove the section "' + (b.title || this.blockTypes[b.type].label) + '"? It disappears from the page when you save.')) return;
            this.form.blocks.splice(i, 1);
            this.openBlock = null;
        },
        addBlock() {
            const type = this.blockTypes[this.newBlockType];
            const block = { type: this.newBlockType };
            type.fields.forEach(f => { block[f.key] = f.type === 'check' ? false : (f.type === 'buttons' ? [] : ''); });
            if (type.items) block.items = [];
            this.form.blocks.push(block);
            this.openBlock = this.form.blocks.length - 1;
            if (type.items) this.addItem(block);
            this.newBlockType = '';
        },
        addItem(block) {
            const item = {};
            this.blockTypes[block.type].items.forEach(f => { item[f.key] = f.type === 'select' ? Object.keys(f.options)[0] : ''; });
            if (!Array.isArray(block.items)) this.$set(block, 'items', []);
            block.items.push(item);
        },
        moveItem(block, i, dir) {
            const it = block.items.splice(i, 1)[0];
            block.items.splice(i + dir, 0, it);
        },
        doDelete() {
            const page = this.deleting;
            axios.delete('/api/admin/site-pages/' + page.id, this.auth())
                .then(res => { this.$toaster.success(res.data.message); this.deleting = null; this.load(); })
                .catch(err => this.$toaster.error((err.response && err.response.data && err.response.data.message) || 'Could not delete the page.'));
        },
    },
    beforeRouteLeave(to, from, next) {
        if (this.dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return next(false);
        next();
    },
    mounted() { this.load(); },
};
</script>

<style scoped>
.spg { padding: 0 4px 60px; }
.spg-top { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 16px; }
.spg-list { background: #fff; border: 1px solid #ebedf3; border-radius: 10px; overflow: hidden; }
.spg-row { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding: 14px 18px; border-bottom: 1px solid #f0f1f4; cursor: pointer; }
.spg-row:last-child { border-bottom: 0; }
.spg-row:hover { background: #fafbfc; }
.spg-row__main { flex: 1; min-width: 200px; display: flex; flex-direction: column; }
.spg-row__main strong { font-size: 15px; color: #111; }
.spg-row__actions { display: flex; gap: 6px; }
.spg-tag { font-size: 11.5px; font-weight: 600; padding: 3px 10px; border-radius: 20px; white-space: nowrap; }
.spg-tag.is-text { background: #eef4ff; color: #2f55a3; }
.spg-tag.is-designed { background: #f3f4f7; color: #666; }
.spg-tag.is-footer { background: #eaf7ee; color: #23804a; }
.spg-tag.is-nofooter { background: #fff6e5; color: #9a6400; }
.spg-confirm { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-top: 12px; padding: 12px 16px; background: #fdecea; border-radius: 8px; }
.spg-card { background: #fff; border: 1px solid #ebedf3; border-radius: 10px; padding: 18px 20px; margin-bottom: 16px; }
.spg-card h5 { font-weight: 700; margin-bottom: 4px; }
.spg-note { background: #f7f8fa; }
.spg-button { display: flex; gap: 6px; margin-bottom: 8px; }
.spg-button input:first-child { flex: 0 0 40%; }
.spg-card >>> .ql-editor { min-height: 320px; font-size: 15px; }
.spg-block { border: 1px solid #ebedf3; border-radius: 8px; margin-bottom: 8px; }
.spg-block.is-open { border-color: #c9ccd6; box-shadow: 0 3px 12px rgba(0,0,0,.06); }
.spg-block__head { display: flex; align-items: center; gap: 12px; padding: 10px 12px; cursor: pointer; }
.spg-block__num { width: 24px; height: 24px; border-radius: 50%; background: #f3f4f7; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0; }
.spg-block__title { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.spg-block__title small { color: #8a8f99; font-size: 11.5px; text-transform: uppercase; letter-spacing: .4px; }
.spg-block__title strong { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.spg-block__tools { display: flex; gap: 4px; }
.spg-block__body { border-top: 1px solid #f0f1f4; padding: 14px 16px 6px; }
.spg-item { background: #f7f8fa; border-radius: 8px; padding: 10px 12px; margin-bottom: 8px; }
.spg-item__head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.spg-add { display: flex; gap: 8px; margin-top: 12px; }
</style>
